import { useState, useRef } from "react";
import MicRecorder from "mic-recorder-to-mp3";
import toastError from "../../../errors/toastError";
import { sendMessage } from "../../../services/messages.api";

const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const useAudioRecorder = ({
  ticketId,
  inputMessage,
  setInputMessage,
  signMessage,
  user,
  setLoading,
}) => {
  const [recording, setRecording] = useState(false);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);

  const _teardownAnalyser = () => {
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.75;
      source.connect(analyser);
      analyserRef.current = analyser;

      await Mp3Recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      _teardownAnalyser();
      toastError(err);
      setLoading(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    _teardownAnalyser();
    try {
      const [, blob] = await Mp3Recorder.stop().getMp3();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `audio-record-site-${new Date().getTime()}.mp3`;
      formData.append("medias", blob, filename);
      formData.append("fromMe", true);
      const caption = inputMessage?.trim() || "";
      if (caption) {
        formData.append(
          "body",
          signMessage ? `*${user?.name}:*\n${caption}` : caption
        );
        setInputMessage("");
      }

      await sendMessage(ticketId, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    _teardownAnalyser();
    try {
      await Mp3Recorder.stop().getMp3();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  return {
    recording,
    setRecording,
    analyserRef,
    handleStartRecording,
    handleUploadAudio,
    handleCancelAudio,
  };
};

export default useAudioRecorder;
